import ClientReportInfo from './ClientReportInfo';
import React from 'react';
import { Box, Button, LinearProgress, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import ClientReportDetails from './ClientReportDetails';
import { downloadReportCsv, reportMetadataRows, reportNumber, reportRate } from './clientPurchaseReportUtils';
import { reportActionSx, reportPanelSx, reportSectionTitleSx, reportTableSx } from './clientReportStyles';

const thresholds = [
  ['exactly_1', 'Exactly 1 purchase'], ['at_least_2', '2+ purchases'],
  ['at_least_3', '3+ purchases'], ['at_least_5', '5+ purchases'],
];
const buckets = [['0', 'No purchases'], ['1', '1 purchase'], ['2', '2 purchases'], ['3_4', '3–4 purchases'], ['5_plus', '5+ purchases']];

export function purchaseBehaviorCsvRows(response) {
  const data = response.purchase_behavior;
  return [
    ['Client purchase behavior'], ...reportMetadataRows(response),
    ['Population', data.population_total],
    ['Time basis', 'Lifetime through as_of; recency is trailing elapsed 24-hour days, independent of the activity period'],
    [], ['Measure', 'Clients', 'Share of current clients %'],
    ...thresholds.map(([key, label]) => [label, data.thresholds[key].count, data.thresholds[key].rate]),
    ...[30, 60, 90].map((days) => [`Purchased in last ${days} days`, data.recent_purchasers[days].count, data.recent_purchasers[days].rate]),
    [], ['Non-overlapping distribution', 'Clients', 'Share %'],
    ...buckets.map(([key, label]) => [label, data.buckets[key].count, data.buckets[key].rate]),
  ];
}

export default function ClientsPurchaseBehavior({ response, status }) {
  const data = response?.purchase_behavior;
  if (!data) return null;
  const loading = status === 'loading';
  return (
    <Stack component="section" aria-label="Client purchase behavior" spacing={1.5} sx={reportPanelSx}>
      <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'flex-start' }} spacing={1}>
        <Box>
          <Typography variant="h6" sx={reportSectionTitleSx}>Purchase behavior<ClientReportInfo label="About purchase behavior">Purchase thresholds and recent windows overlap; do not add them together. The distribution assigns each client to one group. Recent windows use exact elapsed 24-hour days. Counts use available order history. Imported accounts may have earlier purchases that are not recorded here.</ClientReportInfo></Typography>
          <Typography variant="body2" sx={{ color: '#475569' }}>
            {reportNumber(data.population_total)} current clients · {response.filters?.county?.label}
          </Typography>
          <Typography variant="caption" sx={{ color: '#475569' }}>
            As of {new Date(data.as_of).toLocaleString('ro-RO', { timeZone: 'Europe/Bucharest' })} · Europe/Bucharest
          </Typography>
        </Box>
        <Button size="small" variant="outlined" sx={{ ...reportActionSx, flexShrink: 0, display: { xs: 'none', md: 'inline-flex' } }} disabled={status !== 'success'} onClick={() => downloadReportCsv(purchaseBehaviorCsvRows(response), 'client_purchase_behavior.csv')}>Export purchases CSV</Button>
      </Stack>
      {loading && <LinearProgress aria-label="Refreshing purchase behavior" />}
      <Typography sx={{ color: '#475569', fontSize: '0.78rem' }}>All current clients in the selected county · independent of registration dates.</Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' }, gap: 1 }}>
        {thresholds.map(([key, label]) => (
          <Box key={key} sx={{ p: 1.25, bgcolor: '#f8fafc', borderRadius: 2, minWidth: 0 }}>
            <Typography sx={{ color: '#475569', fontSize: '0.8rem', fontWeight: 700 }}>{label}</Typography>
            <Typography sx={{ color: '#0f172a', fontSize: '1.5rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>{reportNumber(data.thresholds[key].count)}</Typography>
            <Typography sx={{ color: '#475569', fontSize: '0.75rem' }}>{reportRate(data.thresholds[key].rate)} of clients</Typography>
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(3, minmax(0, 1fr))' }, gap: 1 }}>
        {[30, 60, 90].map((days) => (
          <Stack key={days} direction={{ xs: 'row', sm: 'column' }} justifyContent="space-between" spacing={0.5} sx={{ p: 1.5, bgcolor: '#f0fdfa', borderRadius: 2 }}>
            <Typography sx={{ fontSize: '0.8rem', color: '#115e59', fontWeight: 700 }}>Purchased in last {days} days</Typography>
            <Box sx={{ textAlign: { xs: 'right', sm: 'left' }, flexShrink: 0 }}>
              <Typography sx={{ color: '#134e4a', fontSize: '1.3rem', fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{reportNumber(data.recent_purchasers[days].count)}</Typography>
              <Typography sx={{ color: '#115e59', fontSize: '0.75rem' }}>{reportRate(data.recent_purchasers[days].rate)} of clients</Typography>
            </Box>
          </Stack>
        ))}
      </Box>
      <ClientReportDetails title="Purchase distribution">
        <Stack spacing={1.25} sx={{ textAlign: 'left' }}>
          <TableContainer sx={{ bgcolor: '#fff', borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Table size="small" aria-label="Lifetime purchase distribution" sx={reportTableSx}>
              <TableHead><TableRow><TableCell>Lifetime purchases</TableCell><TableCell align="right">Clients</TableCell><TableCell align="right">Share</TableCell></TableRow></TableHead>
              <TableBody>{buckets.map(([key, label]) => (
                <TableRow key={key}><TableCell>{label}</TableCell><TableCell align="right">{reportNumber(data.buckets[key].count)}</TableCell><TableCell align="right">{reportRate(data.buckets[key].rate)}</TableCell></TableRow>
              ))}</TableBody>
            </Table>
          </TableContainer>
        </Stack>
      </ClientReportDetails>
    </Stack>
  );
}
